import type { Ecosystem } from './ecosystem.ts';
import type { AuthorisationServerRealm, EcosystemPolicy } from './policy-schema.ts';

/**
 * One stated decision per regulatory ecosystem.
 *
 * Before this record, `ecosystem === 'ofb'` stood in for a dozen independent domain decisions at
 * roughly forty-five call sites across seven packages — the SSA claim profile the DCR verifier
 * enforces, which Keycloak realm carries the ecosystem, which bounded-context schema holds its
 * participants, which Directory host the P1 smoke probes, how the regulatory catalogue groups and
 * authorises families, and three separate operator-facing labels. Each site restated the rule from
 * the member literal, so the vocabulary was closed for extension and open for modification.
 *
 * This mirrors `DeploymentEnvironmentPolicy` in `@finnest/runtime-cd-contract`, which already does
 * exactly this for `dev | sandbox | prod`. The Zod enums in `@finnest/core/schemas` accept exactly
 * these members; they stay frozen until their last Zod-composing importer moves.
 */

/** The catalog's shape: each row's key IS its `ecosystem`, checked by the compiler. */
type EcosystemPolicyCatalog = {
  readonly [Member in Ecosystem]: EcosystemPolicy & { readonly ecosystem: Member };
};

export const ECOSYSTEM_POLICIES = {
  ofb: {
    ecosystem: 'ofb',
    regulator: 'bcb',
    displayName: 'Open Finance Brasil',
    apiFamilyName: 'Open Finance',
    shortLabel: 'OFB',
    authorisationServerRealm: 'finnest-ofb',
    directoryBoundedContext: 'banking',
    softwareStatementOrgIdentifier: 'org-cnpj',
    softwareStatementExpiry: 'required',
    catalogueFamilyGrouping: 'per-family-row',
    cataloguePhaseLabelSource: 'normalised-ofb-phase',
    catalogueScopeAuthority: 'directory-roles',
  },
  opin: {
    ecosystem: 'opin',
    regulator: 'susep',
    displayName: 'Open Insurance Brasil',
    apiFamilyName: 'Open Insurance',
    shortLabel: 'OPIN',
    authorisationServerRealm: 'finnest-opin',
    directoryBoundedContext: 'insurance',
    softwareStatementOrgIdentifier: 'org-number',
    softwareStatementExpiry: 'absent',
    catalogueFamilyGrouping: 'merge-by-insurance-type',
    cataloguePhaseLabelSource: 'raw-track',
    catalogueScopeAuthority: 'insurance-types',
  },
} as const satisfies EcosystemPolicyCatalog;

/** The total lookup. No failure, no optionality: every ecosystem has a complete policy. */
export function ecosystemPolicy(ecosystem: Ecosystem): EcosystemPolicy {
  return ECOSYSTEM_POLICIES[ecosystem];
}

/**
 * A mapped generic is intentional here: this callable capability makes TypeScript reject an
 * incomplete ecosystem dispatch table before the program can run. It is how a per-ecosystem value
 * that is NOT data — a schema, a runner factory, a parser — is bound without a literal comparison.
 */
export type EcosystemRegistry<Value> = Readonly<Record<Ecosystem, Value>>;

export function ecosystemRegistry<Value>(
  registry: EcosystemRegistry<Value>,
): EcosystemRegistry<Value> {
  return Object.freeze(registry);
}

/**
 * Terminates an exhaustive ecosystem dispatch. If Ecosystem gains a member, every caller remains a
 * compile error until it handles the new branch instead of silently falling through.
 *
 * No production `switch` needs it today — every converted site became a registry lookup, which is
 * the stronger shape. It stays because `finnest/closed-vocab-switch-exhaustive` names it as the
 * required terminator for this vocabulary (`_closed-vocab-table.ts`, `remediation.exhaustedFn`):
 * the first author who does need a `switch` must find it here.
 */
export function ecosystemExhausted(ecosystem: never): never {
  return ecosystem;
}

/**
 * The reverse of {@link EcosystemPolicy.authorisationServerRealm}. Two adapters (`as-metadata-port`,
 * `dcr-probe-port`) read the ecosystem back out of the realm name a caller supplied; the mapped type
 * makes the compiler reject an entry that disagrees with the policy catalog.
 */
type EcosystemByAuthorisationServerRealm = {
  readonly [
    Member in Ecosystem as (typeof ECOSYSTEM_POLICIES)[Member]['authorisationServerRealm']
  ]: Member;
};

export const ECOSYSTEM_BY_AUTHORISATION_SERVER_REALM = {
  'finnest-ofb': 'ofb',
  'finnest-opin': 'opin',
} as const satisfies EcosystemByAuthorisationServerRealm;

export function ecosystemForAuthorisationServerRealm(realm: AuthorisationServerRealm): Ecosystem {
  return ECOSYSTEM_BY_AUTHORISATION_SERVER_REALM[realm];
}
